import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "@/lib/api-client.js";
import { useAuthStore } from "@/stores/auth.store.js";
import {
  initialLoginEmail,
  loginNoticeForReason,
  rememberLoginEmail,
} from "./login-helpers.js";

interface LoginResponse {
  token: string;
  userId: string;
  tenantId: string;
  role: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuthStore();
  const [email, setEmail] = useState(() =>
    initialLoginEmail(typeof localStorage !== "undefined" ? localStorage : undefined),
  );
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const notice = loginNoticeForReason(searchParams.get("reason"));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const res = await apiClient.post<LoginResponse>("/auth/login", { email, password });
    if (res.success) {
      const data = res.data;
      rememberLoginEmail(typeof localStorage !== "undefined" ? localStorage : undefined, email);
      login({ token: data.token, userId: data.userId, tenantId: data.tenantId, role: data.role });
      // Installateure landen direkt im Kalkulator (alles andere ist für sie gesperrt).
      void navigate(data.role === "installer" ? "/kalkulation" : "/dashboard");
    } else {
      setError(res.error.message);
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, var(--color-primary) 0%, #2d5488 100%)",
    }}>
      <div style={{ width: 380 }}>
        <div style={{ textAlign: "center", marginBottom: 32, color: "#fff" }}>
          <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 4, letterSpacing: "0.1em" }}>ELMETIC</div>
          <div style={{ fontSize: 28, fontWeight: 700 }}>EnergyOS</div>
          <div style={{ fontSize: 13, opacity: 0.5, marginTop: 4 }}>Österreichische Energieplanungsplattform</div>
        </div>

        <div className="card" style={{ padding: 32 }}>
          <h2 style={{ marginBottom: 24, fontSize: 18, fontWeight: 600, textAlign: "center" }}>Anmelden</h2>

          {notice && (
            <div data-testid="session-expired-notice" style={{ background: "#fffaf0", border: "1px solid #fbd38d", borderRadius: 6, padding: "10px 14px", marginBottom: 16, color: "#7b341e", fontSize: 13 }}>
              {notice}
            </div>
          )}

          <form onSubmit={(e) => void handleSubmit(e)}>
            <div style={{ marginBottom: 16 }}>
              <label>E-Mail</label>
              <input
                data-testid="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label>Passwort</label>
              <input
                data-testid="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div style={{ background: "#fff5f5", border: "1px solid #fed7d7", borderRadius: 6, padding: "10px 14px", marginBottom: 16, color: "var(--color-error)", fontSize: 13 }}>
                {error}
              </div>
            )}

            <button data-testid="login-submit" type="submit" className="btn-primary" style={{ width: "100%", padding: 12 }} disabled={loading}>
              {loading ? "Anmelden…" : "Anmelden"}
            </button>
          </form>

          {import.meta.env.DEV && (
            <div style={{ marginTop: 16, padding: 12, background: "#f7fafc", borderRadius: 6, fontSize: 12, color: "var(--color-text-muted)" }}>
              <strong>Demo-Zugangsdaten:</strong><br />
              admin@elmetic.at / EnergyOS_Admin_2025!
            </div>
          )}
        </div>

        <div style={{ textAlign: "center", marginTop: 16, color: "rgba(255,255,255,0.4)", fontSize: 12 }}>
          Phase 0 · Fundament · {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}
